// Shared UTM types — kept in `shared/` so both the client-side scoring path
// (Papa Parse → score in the browser) and the server-side aggregator (GA4 rows)
// use the same shapes.

export interface UtmRow {
  // Either a full URL or the raw params decomposed.
  url?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  sourcePlatform?: string;
  // Optional weight — session count from GA4, or explicit from an upload.
  // If omitted, the row counts as 1.
  sessions?: number;
}

export type IssueSeverity = "severe" | "medium" | "mild";

export type IssueCategory =
  | "required-params"
  | "canonical-naming"
  | "campaign-structure"
  | "platform-metadata"
  | "content-granularity"
  | "dark-social-leakage"
  // Added with the naming-convention ruleset (see ./naming-conventions.ts).
  // `formatting` covers lowercase / hyphens-not-spaces / no-special-chars.
  // `vocabulary` covers off-allowlist source / medium values + the milder
  // "permitted but too generic" case (e.g. `social` when `organic-social`
  // would be more specific). `anti-pattern` covers banned-vague campaign
  // names and YYYYMMDD-style dates.
  | "formatting"
  | "vocabulary"
  | "anti-pattern";

export interface UtmIssue {
  category: IssueCategory;
  severity: IssueSeverity;
  label: string; // short human label
  detail: string; // why this row triggered it
  examples: string[]; // up to 3 example source/medium/campaign tuples
  rowCount: number; // how many rows this hit
  sessionCount: number; // total weight affected
}

export interface UtmScoreResult {
  score: number; // 0–100
  band: "excellent" | "good" | "needs-work" | "broken";
  issues: UtmIssue[];
  totals: {
    rows: number;
    sessions: number;
    flaggedRows: number;
    flaggedSessions: number;
  };
  // Rows that triggered at least one issue, for the flagged-rows download.
  flagged: Array<UtmRow & { issues: string[] }>;
}

// Canonical taxonomy per PRD §19 / §20.
export const CANONICAL_MEDIUMS = [
  "organic-social",
  "paid-social",
  "influencer",
  "community",
] as const;

export type CanonicalMedium = (typeof CANONICAL_MEDIUMS)[number];

// Canonical platform names → used to normalise messy `utm_source` values.
export const CANONICAL_PLATFORMS = [
  "Instagram",
  "Facebook",
  "LinkedIn",
  "TikTok",
  "Pinterest",
  "YouTube",
  "Threads",
  "X",
  "Google Business",
  "Other Social",
] as const;

export type CanonicalPlatform = (typeof CANONICAL_PLATFORMS)[number];

// Synonym map — lowercase. PRD §18.6. Keep in sync with the backend copy
// used by the GA4 aggregator.
export const SOURCE_TO_PLATFORM: Record<string, CanonicalPlatform> = {
  // Instagram
  instagram: "Instagram",
  ig: "Instagram",
  "instagram.com": "Instagram",
  "l.instagram.com": "Instagram",
  // Facebook
  facebook: "Facebook",
  fb: "Facebook",
  "facebook.com": "Facebook",
  "m.facebook.com": "Facebook",
  "l.facebook.com": "Facebook",
  "lm.facebook.com": "Facebook",
  // LinkedIn
  linkedin: "LinkedIn",
  "linkedin.com": "LinkedIn",
  "lnkd.in": "LinkedIn",
  // TikTok
  tiktok: "TikTok",
  "tiktok.com": "TikTok",
  // X / Twitter
  twitter: "X",
  "twitter.com": "X",
  x: "X",
  "x.com": "X",
  "t.co": "X",
  // Threads
  threads: "Threads",
  "threads.net": "Threads",
  // YouTube
  youtube: "YouTube",
  "youtube.com": "YouTube",
  "youtu.be": "YouTube",
  "m.youtube.com": "YouTube",
  // Pinterest
  pinterest: "Pinterest",
  "pinterest.com": "Pinterest",
  "pin.it": "Pinterest",
  // Google Business
  gmb: "Google Business",
  googlebusiness: "Google Business",
  "google business": "Google Business",
};
